import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Badge } from '../../../components/ui/badge';
import {
  FileText,
  Download,
  X,
  Loader2,
  History,
  RefreshCw,
  AlertTriangle,
  Shield,
  FileCheck,
  GitCompare,
  CheckCircle,
  Info,
} from 'lucide-react';

/**
 * VersionHistoryModal - Regulatory Compliant Version Management (21 CFR Part 11)
 * 
 * Required fields for new version upload:
 * - Update description (mandatory)
 * - Change summary (mandatory)
 * - Workflow status (mandatory)
 * - Reason for update (mandatory)
 * - Reason category (mandatory)
 * - Comparison summary (mandatory, embedded - not separate document)
 */
const VersionHistoryModal = ({
  isOpen,
  onClose,
  selectedDocForVersion,
  documentVersions,
  uploadingVersion,
  handleUploadNewVersion,
  handleDownload,
  handleDownloadVersion,
  isEditor,
  canUpload,
  formatFileSize,
}) => {
  // Use canUpload if available, otherwise fall back to isEditor
  const canUploadNewVersion = canUpload ? canUpload() : (isEditor ? isEditor() : false);
  
  // State for new version upload form
  const [newVersionFile, setNewVersionFile] = useState(null);
  const [formData, setFormData] = useState({
    updateDescription: '',
    changeSummary: '',
    impactedSections: '',
    workflowStatus: 'pending_review',
    assignedReviewers: '',
    reasonForUpdate: '',
    reasonCategory: '',
    comparisonSummary: '',
    comparisonDetails: '',
    effectiveDate: '',
    regulatoryReference: '',
  });
  const [validationErrors, setValidationErrors] = useState([]);
  const [showValidationWarning, setShowValidationWarning] = useState(false);

  const REASON_CATEGORIES = [
    { value: 'correction', label: 'Correction - Fix errors or typos' },
    { value: 'amendment', label: 'Amendment - Formal change to protocol' },
    { value: 'regulatory_update', label: 'Regulatory Update - Compliance requirement' },
    { value: 'error_fix', label: 'Error Fix - Technical or data error' },
    { value: 'content_update', label: 'Content Update - New information' },
    { value: 'format_change', label: 'Format Change - Presentation changes' },
    { value: 'supersede', label: 'Supersede - Replace with new document' },
  ];

  const WORKFLOW_STATUSES = [
    { value: 'draft', label: 'Draft - Not ready for review' },
    { value: 'pending_review', label: 'Pending Review - Ready for review' },
    { value: 'under_qc', label: 'Under QC - Quality check required' },
    { value: 'pending_approval', label: 'Pending Approval - Awaiting final approval' },
  ];

  const validateForm = () => {
    const errors = [];
    
    if (!newVersionFile) {
      errors.push('New version file is required');
    }
    
    if (!formData.updateDescription || formData.updateDescription.length < 10) {
      errors.push('Update description is required (minimum 10 characters)');
    }
    
    if (!formData.changeSummary || formData.changeSummary.length < 10) {
      errors.push('Change summary is required (minimum 10 characters)');
    }
    
    if (!formData.workflowStatus) {
      errors.push('Workflow status is required');
    }
    
    if (!formData.reasonForUpdate || formData.reasonForUpdate.length < 10) {
      errors.push('Reason for update is required (minimum 10 characters)');
    }
    
    if (!formData.reasonCategory) {
      errors.push('Reason category is required');
    }
    
    if (!formData.comparisonSummary || formData.comparisonSummary.length < 10) {
      errors.push('Comparison summary is required (minimum 10 characters) - must describe differences between versions');
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowValidationWarning(true);
      return;
    }
    
    setShowValidationWarning(false);
    
    // Call parent handler with regulatory-compliant data
    await handleUploadNewVersion({
      file: newVersionFile,
      ...formData,
    });
    
    // Reset form on success
    setNewVersionFile(null);
    setFormData({
      updateDescription: '',
      changeSummary: '',
      impactedSections: '',
      workflowStatus: 'pending_review',
      assignedReviewers: '',
      reasonForUpdate: '',
      reasonCategory: '',
      comparisonSummary: '',
      comparisonDetails: '',
      effectiveDate: '',
      regulatoryReference: '',
    });
    setValidationErrors([]);
  };

  const handleClose = () => {
    setNewVersionFile(null);
    setValidationErrors([]);
    setShowValidationWarning(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] bg-slate-50 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
            <History className="w-5 h-5 text-red-600" />
            Version History - {selectedDocForVersion?.name}
          </DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400">
            21 CFR Part 11 Compliant Version Management
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="history" className="w-full">
          <TabsList className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600">
            <TabsTrigger value="history" className="flex-1 data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-700 dark:text-slate-300">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
            {canUploadNewVersion && (
              <TabsTrigger value="upload" className="flex-1 data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-700 dark:text-slate-300">
                <RefreshCw className="w-4 h-4 mr-2" />
                Upload New Version
              </TabsTrigger>
            )}
          </TabsList>
          
          {/* HISTORY TAB */}
          <TabsContent value="history" className="py-4">
            <ScrollArea className="h-80">
              <div className="space-y-3">
                {/* Current Version */}
                <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-red-600 text-white">v{selectedDocForVersion?.version || 1}</Badge>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">Current Version</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => selectedDocForVersion && handleDownload(selectedDocForVersion)}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-2">
                    {selectedDocForVersion?.uploaded_by_name} • {selectedDocForVersion?.updated_at && new Date(selectedDocForVersion.updated_at).toLocaleString()}
                  </p>
                  {selectedDocForVersion?.latest_update_metadata && (
                    <div className="mt-2 p-2 bg-slate-100 dark:bg-slate-700 rounded text-xs">
                      <p className="text-slate-700 dark:text-slate-300">
                        <strong>Last Update:</strong> {selectedDocForVersion.latest_update_metadata.reason_category} - {selectedDocForVersion.latest_update_metadata.reason_for_update}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Previous Versions */}
                {documentVersions.length === 0 ? (
                  <div className="text-center py-8">
                    <FileCheck className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                    <p className="text-sm text-slate-500">No previous versions</p>
                  </div>
                ) : (
                  documentVersions.map((version) => (
                    <div key={version.id} className="p-4 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-100 dark:bg-slate-800/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">v{version.version}</Badge>
                          <span className="text-sm text-slate-600 dark:text-slate-400">{formatFileSize(version.file_size)}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadVersion(version)}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {version.uploaded_by_name} • {new Date(version.created_at).toLocaleString()}
                      </p>
                      
                      {/* Show embedded comparison if available */}
                      {version.version_comparison && (
                        <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded">
                          <div className="flex items-center gap-2 mb-2">
                            <GitCompare className="w-4 h-4 text-amber-500" />
                            <span className="text-xs font-medium text-amber-600 dark:text-amber-400">
                              Comparison: v{version.version_comparison.old_version} → v{version.version_comparison.new_version}
                            </span>
                          </div>
                          <p className="text-xs text-slate-600 dark:text-slate-400">
                            {version.version_comparison.comparison_summary}
                          </p>
                        </div>
                      )}
                      
                      {/* Show update metadata if available */}
                      {version.update_metadata && (
                        <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/30 rounded text-xs">
                          <p className="text-blue-700 dark:text-blue-300">
                            <strong>Reason:</strong> {version.update_metadata.reason_category} - {version.update_metadata.reason_for_update}
                          </p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </TabsContent>
          
          {/* UPLOAD NEW VERSION TAB - Regulatory Compliant Form */}
          {canUploadNewVersion && (
            <TabsContent value="upload" className="py-4">
              <ScrollArea className="h-[500px] pr-4">
                {/* Compliance Banner */}
                <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500/30 rounded-lg flex items-start gap-3">
                  <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-200 text-sm">21 CFR Part 11 Compliance Required</h4>
                    <p className="text-xs text-amber-300/80 mt-1">
                      All fields marked with * are mandatory. Version comparison must be embedded (not uploaded separately).
                    </p>
                  </div>
                </div>
                
                {/* Validation Errors */}
                {showValidationWarning && validationErrors.length > 0 && (
                  <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <span className="font-medium text-red-400">Compliance Validation Failed</span>
                    </div>
                    <ul className="list-disc list-inside text-xs text-red-300 space-y-1">
                      {validationErrors.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="space-y-4">
                  {/* File Upload */}
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">New Version File *</Label>
                    <div className="mt-1 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4 text-center bg-slate-100 dark:bg-slate-800/80">
                      {newVersionFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="w-5 h-5 text-red-600" />
                          <span className="text-sm font-medium">{newVersionFile.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => setNewVersionFile(null)} className="h-6 w-6 p-0">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <label className="cursor-pointer">
                          <RefreshCw className="w-6 h-6 mx-auto text-slate-500 mb-1" />
                          <p className="text-xs text-slate-500">Click to select file</p>
                          <input type="file" className="hidden" onChange={(e) => setNewVersionFile(e.target.files[0])} />
                        </label>
                      )}
                    </div>
                  </div>
                  
                  {/* Update Description */}
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Update Description * <span className="text-xs text-slate-500">(min 10 chars)</span></Label>
                    <Textarea
                      value={formData.updateDescription}
                      onChange={(e) => setFormData({...formData, updateDescription: e.target.value})}
                      placeholder="Describe what was changed in this version..."
                      className="mt-1 bg-white dark:bg-slate-700 min-h-[60px]"
                    />
                  </div>
                  
                  {/* Change Summary */}
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Change Summary * <span className="text-xs text-slate-500">(min 10 chars)</span></Label>
                    <Textarea
                      value={formData.changeSummary}
                      onChange={(e) => setFormData({...formData, changeSummary: e.target.value})}
                      placeholder="Summarize the key changes made..."
                      className="mt-1 bg-white dark:bg-slate-700 min-h-[60px]"
                    />
                  </div>
                  
                  {/* Impacted Sections */}
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Impacted Sections</Label>
                    <Input
                      value={formData.impactedSections}
                      onChange={(e) => setFormData({...formData, impactedSections: e.target.value})}
                      placeholder="e.g., Section 3.2, Appendix A"
                      className="mt-1 bg-white dark:bg-slate-700"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Workflow Status */}
                    <div>
                      <Label className="text-slate-700 dark:text-slate-300">Workflow Status *</Label>
                      <Select value={formData.workflowStatus} onValueChange={(v) => setFormData({...formData, workflowStatus: v})}>
                        <SelectTrigger className="mt-1 bg-white dark:bg-slate-700">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          {WORKFLOW_STATUSES.map((status) => (
                            <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Reason Category */}
                    <div>
                      <Label className="text-slate-700 dark:text-slate-300">Reason Category *</Label>
                      <Select value={formData.reasonCategory} onValueChange={(v) => setFormData({...formData, reasonCategory: v})}>
                        <SelectTrigger className="mt-1 bg-white dark:bg-slate-700">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {REASON_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Reason for Update */}
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Reason for Update * <span className="text-xs text-slate-500">(min 10 chars)</span></Label>
                    <Textarea
                      value={formData.reasonForUpdate}
                      onChange={(e) => setFormData({...formData, reasonForUpdate: e.target.value})}
                      placeholder="Why is this new version being uploaded?"
                      className="mt-1 bg-white dark:bg-slate-700 min-h-[60px]"
                    />
                  </div>
                  
                  {/* EMBEDDED Comparison Summary - NOT a separate document */}
                  <div className="p-3 border-2 border-amber-500/50 rounded-lg bg-amber-500/10">
                    <div className="flex items-center gap-2 mb-2">
                      <GitCompare className="w-4 h-4 text-amber-500" />
                      <Label className="text-amber-600 dark:text-amber-400 font-medium">Version Comparison (Embedded) *</Label>
                    </div>
                    <p className="text-xs text-amber-300/80 mb-2 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      This comparison will be stored with the version record, NOT as a separate document.
                    </p>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-slate-700 dark:text-slate-300 text-xs">Comparison Summary * <span className="text-slate-500">(min 10 chars)</span></Label>
                        <Textarea
                          value={formData.comparisonSummary}
                          onChange={(e) => setFormData({...formData, comparisonSummary: e.target.value})}
                          placeholder="Describe the key differences between the old and new version..."
                          className="mt-1 bg-white dark:bg-slate-700 min-h-[60px]"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 dark:text-slate-300 text-xs">Comparison Details (Optional)</Label>
                        <Textarea
                          value={formData.comparisonDetails}
                          onChange={(e) => setFormData({...formData, comparisonDetails: e.target.value})}
                          placeholder="Detailed line-by-line or section-by-section comparison..."
                          className="mt-1 bg-white dark:bg-slate-700 min-h-[80px]"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Effective Date */}
                    <div>
                      <Label className="text-slate-700 dark:text-slate-300">Effective Date</Label>
                      <Input
                        type="date"
                        value={formData.effectiveDate}
                        onChange={(e) => setFormData({...formData, effectiveDate: e.target.value})}
                        className="mt-1 bg-white dark:bg-slate-700"
                      />
                    </div>
                    
                    {/* Regulatory Reference */}
                    <div>
                      <Label className="text-slate-700 dark:text-slate-300">Regulatory Reference</Label>
                      <Input
                        value={formData.regulatoryReference}
                        onChange={(e) => setFormData({...formData, regulatoryReference: e.target.value})}
                        placeholder="e.g., FDA-2024-1234"
                        className="mt-1 bg-white dark:bg-slate-700"
                      />
                    </div>
                  </div>
                  
                  {/* Submit Button */}
                  <Button 
                    onClick={handleSubmit} 
                    disabled={uploadingVersion} 
                    className="w-full rounded-full bg-red-600 hover:bg-red-700 text-white mt-4"
                  >
                    {uploadingVersion ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Upload Compliant Version
                      </>
                    )}
                  </Button>
                </div>
              </ScrollArea>
            </TabsContent>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default VersionHistoryModal;
